/**
 * @file SiteDraftStrip.tsx
 * @description Полоса черновика «вида сайта» (phase19.16 Р1; uiux-design-r1
 * §0.1, §6 «Не хватает прав»; SB60, SB79).
 *
 * Видна только при непустом черновике, первой строкой содержимого:
 * «Не опубликовано: оформление 3, меню 1 · последняя правка — Анна Кравец,
 * сегодня 14:32» и кнопки «Что изменено», «Предпросмотр сайта». Если
 * опубликовать нельзя — текстом, чьи правки ждут и у кого есть право (на
 * касании подсказки не работают). До 1024px полоса целиком — кнопка,
 * открывающая шторку «Что изменено».
 */

import { ChevronRight } from "lucide-react";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { hasDraftChanges, useSiteView } from "./site-view-context";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import {
  blockedExplanation,
  discardableParts,
  draftSummary,
  formatEditMoment,
  lastEdit,
  partsWithEditors,
} from "../../lib/site-view/changes";

export function SiteDraftStrip() {
  const view = useSiteView();
  const narrow = useMediaQuery(BELOW_LG_QUERY);
  const changes = view.changes;
  if (!changes || !hasDraftChanges(changes)) return null;

  const blocked = !changes.canPublish && changes.blockedBy.length > 0;
  const edit = lastEdit(changes);
  const summary = blocked ? partsWithEditors(changes, view.currentUserId) : draftSummary(changes);
  const who = !blocked && edit ? ` · последняя правка — ${edit.by.name}, ${formatEditMoment(edit.at)}` : "";
  const text = `Не опубликовано: ${summary}${who}`;

  if (narrow && !blocked) {
    return (
      <button
        type="button"
        onClick={() => view.openDialog("changes")}
        className="flex min-h-12 w-full items-center gap-3 rounded-xl bg-warning/12 px-4 py-3 text-left text-sm text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-warning" />
        <span className="min-w-0 flex-1">{text}</span>
        <ChevronRight aria-hidden className="size-5 shrink-0 text-on-surface-variant" />
      </button>
    );
  }

  const canDiscardMine = discardableParts(changes).length > 0;
  return (
    <NoticeStrip tone="warning">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <span>{text}</span>
          {blocked ? <span className="text-on-surface-variant">{blockedExplanation(changes, view.currentUserId)}</span> : null}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" onClick={() => view.openDialog("changes")}>
            Что изменено
          </Button>
          <Button variant="outline" onClick={() => view.openDialog("preview")}>
            Предпросмотр сайта
          </Button>
          {blocked && canDiscardMine ? (
            <Button variant="ghost" onClick={() => view.openDialog("discard")}>
              Отменить мои правки
            </Button>
          ) : null}
        </div>
      </div>
    </NoticeStrip>
  );
}
